import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { UserCommunication, ViewType } from '@this+that/models';

const GET_PAGINATED_USER_EMAIL_COMMUNICATIONS = gql`
  query getPaginatedUserEmailCommunications(
    $limit: Int!
    $viewTypes: [String!]!
    $exclusiveStartKey: String
    $emailAddresses: [String!]
  ) {
    getPaginatedUserEmailCommunications(
      limit: $limit
      viewTypes: $viewTypes
      exclusiveStartKey: $exclusiveStartKey
      emailAddresses: $emailAddresses
    ) {
      userCommunications {
        userId
        messageId
        userEmailAddress
        createdAt
        author
        authorUserId
        authorEmail
        authorAvatarUrl
        messagePreview
        channel
        channelName
        threadMessageId
        isThreadReply
        threadTimestamp
        recipients {
          userId
          email
          name
          slackUserId
          avatarUrl
          type
        }
        subject
        platform
        messageUrl
        labelIds
        category
        s3Key
        inboxEntryIds
        draftId
        hasUnsubscribeLink
        isUnsubscribed
        calendarEventData {
          eventId
          icalUid
          responseStatus
          summary
          organizer {
            name
            email
          }
          eventTime {
            startTime
            endTime
            timeZone
          }
        }
        threadMessages {
          userId
          messageId
          userEmailAddress
          createdAt
          author
          authorUserId
          authorEmail
          authorAvatarUrl
          messagePreview
          channel
          channelName
          threadMessageId
          isThreadReply
          threadTimestamp
          recipients {
            userId
            email
            name
            slackUserId
            avatarUrl
            type
          }
          subject
          platform
          messageUrl
          labelIds
          category
          s3Key
          inboxEntryIds
          draftId
          hasUnsubscribeLink
          isUnsubscribed
          calendarEventData {
            eventId
            icalUid
            responseStatus
            summary
            organizer {
              name
              email
            }
            eventTime {
              startTime
              endTime
              timeZone
            }
          }
        }
      }
      exclusiveStartKey
    }
  }
`;

interface GetPaginatedUserEmailCommunicationsParams {
  limit: number;
  viewTypes: ViewType[];
  exclusiveStartKey?: string;
  emailAddresses?: string[];
}

interface GetPaginatedUserEmailCommunicationsData {
  getPaginatedUserEmailCommunications: {
    userCommunications: UserCommunication[];
    exclusiveStartKey: string | null;
  };
}

interface GetPaginatedUserCommunicationsByThreadData {
  getPaginatedUserCommunicationsByThread: {
    userCommunications: UserCommunication[];
    exclusiveStartKey: string | null;
  };
}

interface GetUserCommunicationByIdData {
  getUserCommunicationById: UserCommunication | null;
}

interface GeneratePresignedUrlForUserContentData {
  generatePresignedUrlForUserContent: string;
}

export function useGetPaginatedUserEmailCommunications() {
  const { loading, refetch } =
    useQuery<GetPaginatedUserEmailCommunicationsData>(
      GET_PAGINATED_USER_EMAIL_COMMUNICATIONS,
      {
        skip: true,
      },
    );

  const getPaginatedUserEmailCommunications = useCallback(
    async ({
      limit,
      viewTypes,
      exclusiveStartKey,
      emailAddresses,
    }: GetPaginatedUserEmailCommunicationsParams) => {
      const { data } = await refetch({
        limit,
        viewTypes,
        exclusiveStartKey,
        emailAddresses,
      });

      return (
        data?.getPaginatedUserEmailCommunications ?? {
          userCommunications: [],
          exclusiveStartKey: null,
        }
      );
    },
    [refetch],
  );

  return { getPaginatedUserEmailCommunications, loading };
}

const GET_PAGINATED_USER_COMMUNICATIONS_BY_THREAD = gql`
  query getPaginatedUserCommunicationsByThread(
    $threadMessageId: String!
    $limit: Int!
    $exclusiveStartKey: String
  ) {
    getPaginatedUserCommunicationsByThread(
      threadMessageId: $threadMessageId
      limit: $limit
      exclusiveStartKey: $exclusiveStartKey
    ) {
      userCommunications {
        userId
        messageId
        userEmailAddress
        createdAt
        author
        authorUserId
        authorEmail
        authorAvatarUrl
        messagePreview
        messageText
        messageRawText
        channel
        channelType
        channelName
        threadMessageId
        isThreadReply
        threadTimestamp
        recipients {
          userId
          email
          name
          slackUserId
          avatarUrl
          type
        }
        subject
        platform
        messageUrl
        labelIds
        category
        s3Key
        attachments {
          fileName
          fileType
          content
          size
          previewUrl
          contentUrl
          alt
        }
        inboxEntryIds
        slackWorkspaceId
        slackThreadTimestamp
      }
      exclusiveStartKey
    }
  }
`;

interface GetPaginatedUserCommunicationsByThreadParams {
  threadMessageId: string;
  limit: number;
  exclusiveStartKey?: string;
}

export function useGetPaginatedUserCommunicationsByThread() {
  const { loading, refetch } =
    useQuery<GetPaginatedUserCommunicationsByThreadData>(
      GET_PAGINATED_USER_COMMUNICATIONS_BY_THREAD,
      {
        skip: true,
      },
    );

  const getPaginatedUserCommunicationsByThread = useCallback(
    async ({
      threadMessageId,
      limit,
      exclusiveStartKey,
    }: GetPaginatedUserCommunicationsByThreadParams) => {
      const { data } = await refetch({
        threadMessageId,
        limit,
        exclusiveStartKey,
      });

      return data?.getPaginatedUserCommunicationsByThread as {
        userCommunications: UserCommunication[];
        exclusiveStartKey: string | null;
      };
    },
    [refetch],
  );

  return { getPaginatedUserCommunicationsByThread, loading };
}

interface GetPaginatedUserCommunicationsByChannelData {
  getPaginatedUserCommunicationsByChannel: {
    userCommunications: UserCommunication[];
    exclusiveStartKey: string | null;
  };
}

const GET_PAGINATED_USER_COMMUNICATIONS_BY_CHANNEL = gql`
  query getPaginatedUserCommunicationsByChannel(
    $channelId: String!
    $limit: Int!
    $exclusiveStartKey: String
  ) {
    getPaginatedUserCommunicationsByChannel(
      channelId: $channelId
      limit: $limit
      exclusiveStartKey: $exclusiveStartKey
    ) {
      userCommunications {
        userId
        messageId
        userEmailAddress
        createdAt
        author
        authorUserId
        authorEmail
        authorAvatarUrl
        messagePreview
        messageText
        messageRawText
        channel
        channelType
        channelName
        threadMessageId
        isThreadReply
        threadTimestamp
        recipients {
          userId
          email
          name
          slackUserId
          avatarUrl
          type
        }
        subject
        platform
        messageUrl
        labelIds
        category
        s3Key
        attachments {
          fileName
          fileType
          content
          size
          previewUrl
          contentUrl
          alt
        }
        inboxEntryIds
        slackWorkspaceId
        slackThreadTimestamp
      }
      exclusiveStartKey
    }
  }
`;

interface GetPaginatedUserCommunicationsByChannelParams {
  channelId: string;
  limit: number;
  exclusiveStartKey?: string;
}

export function useGetPaginatedUserCommunicationsByChannel() {
  const { loading, refetch } =
    useQuery<GetPaginatedUserCommunicationsByChannelData>(
      GET_PAGINATED_USER_COMMUNICATIONS_BY_CHANNEL,
      {
        skip: true,
      },
    );

  const getPaginatedUserCommunicationsByChannel = useCallback(
    async ({
      channelId,
      limit,
      exclusiveStartKey,
    }: GetPaginatedUserCommunicationsByChannelParams) => {
      const { data } = await refetch({
        channelId,
        limit,
        exclusiveStartKey,
      });

      return data?.getPaginatedUserCommunicationsByChannel as {
        userCommunications: UserCommunication[];
        exclusiveStartKey: string | null;
      };
    },
    [refetch],
  );

  return { getPaginatedUserCommunicationsByChannel, loading };
}

const GET_USER_COMMUNICATION_BY_ID = gql`
  query getUserCommunicationById($messageId: String!) {
    getUserCommunicationById(messageId: $messageId) {
      userId
      messageId
      userEmailAddress
      createdAt
      author
      authorUserId
      authorEmail
      authorAvatarUrl
      messagePreview
      messageRawText
      messageText
      channel
      channelName
      channelType
      threadMessageId
      isThreadReply
      threadTimestamp
      recipients {
        userId
        email
        name
        slackUserId
        avatarUrl
        type
      }
      subject
      platform
      messageUrl
      labelIds
      category
      s3Key
      inboxEntryIds
      draftId
      slackWorkspaceId
      slackThreadTimestamp
      hasUnsubscribeLink
      isUnsubscribed
      calendarEventData {
        eventId
        icalUid
        responseStatus
        summary
        organizer {
          name
          email
        }
        eventTime {
          startTime
          endTime
          timeZone
        }
      }
      attachments {
        fileName
        fileType
        content
        size
        previewUrl
        contentUrl
        alt
      }
      threadMessages {
        userId
        messageId
        userEmailAddress
        createdAt
        author
        authorUserId
        authorEmail
        authorAvatarUrl
        messagePreview
        channel
        channelName
        threadMessageId
        isThreadReply
        threadTimestamp
        recipients {
          userId
          email
          name
          slackUserId
          avatarUrl
          type
        }
        subject
        platform
        messageUrl
        labelIds
        category
        s3Key
        inboxEntryIds
        draftId
        hasUnsubscribeLink
        isUnsubscribed
        calendarEventData {
          eventId
          icalUid
          responseStatus
          summary
          organizer {
            name
            email
          }
          eventTime {
            startTime
            endTime
            timeZone
          }
        }
        attachments {
          fileName
          fileType
          content
          size
          previewUrl
          contentUrl
          alt
        }
      }
    }
  }
`;

export function useGetUserCommunicationById() {
  const { loading, refetch } = useQuery<GetUserCommunicationByIdData>(
    GET_USER_COMMUNICATION_BY_ID,
    {
      skip: true,
    },
  );

  const getUserCommunicationById = useCallback(
    async (messageId: string) => {
      const { data } = await refetch({
        messageId,
      });

      return data?.getUserCommunicationById;
    },
    [refetch],
  );

  return { getUserCommunicationById, loading };
}

const GET_USER_COMMUNICATION_BY_INTERNET_MESSAGE_ID = gql`
  query getUserCommunicationByInternetMessageId($internetMessageId: String!) {
    getUserCommunicationByInternetMessageId(
      internetMessageId: $internetMessageId
    ) {
      userId
      messageId
      userEmailAddress
      createdAt
      author
      authorUserId
      authorEmail
      authorAvatarUrl
      messagePreview
      messageRawText
      messageText
      channel
      channelName
      channelType
      threadMessageId
      isThreadReply
      threadTimestamp
      recipients {
        userId
        email
        name
        slackUserId
        avatarUrl
        type
      }
      subject
      platform
      messageUrl
      labelIds
      category
      s3Key
      inboxEntryIds
      draftId
      slackWorkspaceId
      slackThreadTimestamp
      hasUnsubscribeLink
      isUnsubscribed
      calendarEventData {
        eventId
        icalUid
        responseStatus
        summary
        organizer {
          name
          email
        }
        eventTime {
          startTime
          endTime
          timeZone
        }
      }
      attachments {
        fileName
        fileType
        content
        size
        previewUrl
        contentUrl
        alt
      }
      threadMessages {
        userId
        messageId
        userEmailAddress
        createdAt
        author
        authorUserId
        authorEmail
        authorAvatarUrl
        messagePreview
        channel
        channelName
        threadMessageId
        isThreadReply
        threadTimestamp
        recipients {
          userId
          email
          name
          slackUserId
          avatarUrl
          type
        }
        subject
        platform
        messageUrl
        labelIds
        category
        s3Key
        inboxEntryIds
        draftId
        hasUnsubscribeLink
        isUnsubscribed
        calendarEventData {
          eventId
          icalUid
          responseStatus
          summary
          organizer {
            name
            email
          }
          eventTime {
            startTime
            endTime
            timeZone
          }
        }
        attachments {
          fileName
          fileType
          content
          size
          previewUrl
          contentUrl
          alt
        }
      }
    }
  }
`;

interface GetUserCommunicationByInternetMessageIdData {
  getUserCommunicationByInternetMessageId: UserCommunication | null;
}

export function useGetUserCommunicationByInternetMessageId() {
  const { loading, refetch } =
    useQuery<GetUserCommunicationByInternetMessageIdData>(
      GET_USER_COMMUNICATION_BY_INTERNET_MESSAGE_ID,
      {
        skip: true,
      },
    );

  const getUserCommunicationByInternetMessageId = useCallback(
    async (internetMessageId: string) => {
      const { data } = await refetch({
        internetMessageId,
      });

      return data?.getUserCommunicationByInternetMessageId;
    },
    [refetch],
  );

  return { getUserCommunicationByInternetMessageId, loading };
}

const GENERATE_PRESIGNED_URL_FOR_USER_CONTENT = gql`
  query generatePresignedUrlForUserContent($s3Key: String!) {
    generatePresignedUrlForUserContent(s3Key: $s3Key)
  }
`;

export function useGeneratePresignedUrlForUserContent() {
  const { loading, refetch } = useQuery<GeneratePresignedUrlForUserContentData>(
    GENERATE_PRESIGNED_URL_FOR_USER_CONTENT,
    {
      skip: true,
    },
  );

  const generatePresignedUrlForUserContent = useCallback(
    async (s3Key: string) => {
      const { data } = await refetch({
        s3Key,
      });

      return data?.generatePresignedUrlForUserContent;
    },
    [refetch],
  );

  return { generatePresignedUrlForUserContent, loading };
}

const GET_EMAIL_COUNTERS = gql`
  query getEmailCounters {
    getEmailCounters {
      inbox
      social
      updates
      forums
      promotions
    }
  }
`;

interface GetEmailCountersData {
  getEmailCounters: {
    inbox: number;
    social: number;
    updates: number;
    forums: number;
    promotions: number;
  };
}

export function useGetEmailCounters() {
  const { data, loading, refetch } = useQuery<GetEmailCountersData>(
    GET_EMAIL_COUNTERS,
    { pollInterval: 30_000 },
  );

  return {
    emailCounters: data?.getEmailCounters ?? null,
    loading,
    refetch,
  };
}

const RESET_EMAIL_TAB_COUNTER = gql`
  mutation resetEmailTabCounter($tab: EmailTab!) {
    resetEmailTabCounter(tab: $tab)
  }
`;

export function useResetEmailTabCounter() {
  const [resetMutation] = useMutation(RESET_EMAIL_TAB_COUNTER, {
    refetchQueries: [{ query: GET_EMAIL_COUNTERS }],
  });

  const resetEmailTabCounter = useCallback(
    (tab: string) => resetMutation({ variables: { tab } }),
    [resetMutation],
  );

  return { resetEmailTabCounter };
}
